import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { toast } from '@/hooks/use-toast';
import { FormField, useFieldValidation, validators } from '@/components/ui/form-field';
import { cn } from '@/lib/utils';
import { apiClient } from '@/lib/api';
import { CountdownTimer } from '@/components/ui/countdown-timer';
import { Loader2, ArrowLeft, MailCheck } from 'lucide-react';

export function ForgotPasswordPage() {
  useDocumentTitle('Forgot Password');

  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sent, setSent] = useState(false);  const [error, setError] = useState('');
  const [rateLimitInfo, setRateLimitInfo] = useState<{
    remaining: number;
    limit: number;
    resetAt: number | undefined;
  } | null>(null);

  // Inline validation
  const emailValidation = useFieldValidation([validators.required(), validators.email()]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setRateLimitInfo(null);

    if (!email.trim()) {
      setError(t('errors.pleaseEnterEmail'));
      return;
    }

    setIsLoading(true);

    try {
      const res = await apiClient.post('/auth/reset-password', { email });

      if (res.success) {
        setSent(true);
        toast({
          title: t('auth.resetLinkSent'),
          description: t('auth.resetLinkSentDesc'),
          variant: 'success',
        });
      }
    } catch (err: any) {
      // Capture rate limit info — don't transition to success view if rate limited
      if (err.rateLimitRemaining !== undefined) {
        setRateLimitInfo({
          remaining: err.rateLimitRemaining,
          limit: err.rateLimitLimit || 3,
          resetAt: err.rateLimitReset,
        });
      } else {
        // For non-rate-limit errors, still show success to prevent email enumeration
        setSent(true);
        toast({
          title: t('auth.resetLinkSent'),
          description: t('auth.resetLinkSentDesc'),
          variant: 'success',
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Success view
  if (sent) {
    return (
      <div className="min-h-screen bg-[var(--bg-primary)] flex items-center justify-center p-4">
        <div className="bg-noise opacity-[0.02] pointer-events-none fixed inset-0" />

        <div className="w-full max-w-md relative z-10">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold tracking-tight mb-2">
              Dyn<span className="text-dyndev-green">Dev</span>
            </h1>
            <p className="text-[var(--text-secondary)]">{t('app.tagline')}</p>
          </div>

          <Card className="shadow-xl">
            <CardHeader>
              <CardTitle className="text-xl">{t('auth.checkEmail')}</CardTitle>
            </CardHeader>
            <CardContent className="text-center">
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 rounded-full bg-dyndev-green/10 flex items-center justify-center">
                  <MailCheck size={32} className="text-dyndev-green" />
                </div>
              </div>

              <p className="text-sm text-[var(--text-secondary)] mb-6">
                {t('auth.checkEmailDesc', { email })}
              </p>

              <p className="text-xs text-[var(--text-secondary)] mb-6">
                {t('auth.checkEmailSpam')}{' '}
                <button
                  onClick={() => { setSent(false); setEmail(''); }}
                  className="text-dyndev-green hover:text-dyndev-green/80 transition-colors font-medium"
                >
                  {t('auth.tryAgain')}
                </button>
                .
              </p>

              <Link
                to="/login"
                className="inline-flex items-center gap-1 text-sm text-dyndev-green hover:text-dyndev-green/80 transition-colors font-medium"
              >
                <ArrowLeft size={14} />
                {t('auth.backToSignIn')}
              </Link>
            </CardContent>
          </Card>

          <p className="text-center mt-6 text-xs text-[var(--text-secondary)]">
            &copy; {new Date().getFullYear()} DynDev. {t('app.copyright')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--bg-primary)] flex items-center justify-center p-4">
      {/* Noise overlay */}
      <div className="bg-noise opacity-[0.02] pointer-events-none fixed inset-0" />

      <div className="w-full max-w-md relative z-10">
        {/* Logo */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold tracking-tight mb-2">
            Dyn<span className="text-dyndev-green">Dev</span>
          </h1>
          <p className="text-[var(--text-secondary)]">{t('app.tagline')}</p>
        </div>

        {/* Forgot Password Card */}
        <Card className="shadow-xl">
          <CardHeader>
            <CardTitle className="text-xl">{t('auth.forgotPasswordTitle')}</CardTitle>
          </CardHeader>
          <CardContent>              <p className="text-sm text-[var(--text-secondary)] mb-6">
                {t('auth.forgotPasswordDesc')}
              </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                  {error}
                </div>
              )}

              {/* Rate limit indicator */}
              {rateLimitInfo && (
                <div className="space-y-2">
                  {rateLimitInfo.remaining > 0 ? (
                    <div className="p-2.5 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-400 text-xs flex items-center gap-2">
                      <div className="flex-1">
                        <span className="font-medium">{rateLimitInfo.remaining}</span>
                        <span className="text-[var(--text-secondary)]"> of </span>
                        <span className="font-medium">{rateLimitInfo.limit}</span>
                        <span className="text-[var(--text-secondary)]"> password reset attempts remaining</span>
                      </div>
                      <div className="w-16 h-1.5 rounded-full bg-[var(--bg-primary)] overflow-hidden flex-shrink-0">
                        <div
                          className="h-full rounded-full bg-amber-400 transition-all duration-300"
                          style={{ width: `${(rateLimitInfo.remaining / rateLimitInfo.limit) * 100}%` }}
                        />
                      </div>
                    </div>                    ) : (
                    <div className="p-2.5 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-xs">
                      <span className="font-medium">Too many password reset attempts.</span>
                      <span className="text-[var(--text-secondary)]">
                        {' '}Try again in{' '}
                        {rateLimitInfo.resetAt ? (
                          <CountdownTimer
                            key={rateLimitInfo.resetAt}
                            resetAt={rateLimitInfo.resetAt}
                            onExpired={() => setRateLimitInfo(null)}
                          />
                        ) : (
                          'a few minutes'
                        )}
                        .
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Email field with inline validation */}
              <FormField
                label={t('auth.email')}
                error={emailValidation.error}
                touched={emailValidation.touched}
                dirty={emailValidation.dirty}
                showError={emailValidation.showError}
                isValid={emailValidation.isValid}
              >
                <div className={`relative rounded-lg transition-all duration-300 ${emailValidation.showError ? 'ring-2 ring-red-400/30 ring-offset-2 ring-offset-[var(--bg-primary)]' : ''}`}>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      emailValidation.onChange(e);
                      if (rateLimitInfo) setRateLimitInfo(null);
                    }}
                    onBlur={emailValidation.onBlur}
                    placeholder={t('auth.emailPlaceholder')}
                    aria-label={t('auth.email')}
                    required
                    className={cn(
                      emailValidation.showError && 'border-red-400/50 focus:border-red-400 focus:ring-red-400',
                      emailValidation.isValid && emailValidation.dirty && 'border-dyndev-green/50',
                    )}
                  />
                </div>
              </FormField>

              <Button
                type="submit"
                disabled={isLoading || !email.trim()}
                size="pill"
                className="w-full"
              >
                {isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {isLoading ? t('auth.sending') : t('auth.sendResetLink')}
              </Button>
            </form>

            <div className="text-center mt-6">
              <Link
                to="/login"
                className="inline-flex items-center gap-1 text-sm text-dyndev-green hover:text-dyndev-green/80 transition-colors font-medium"
              >
                <ArrowLeft size={14} />
                {t('auth.backToSignIn')}
              </Link>
            </div>
          </CardContent>
        </Card>

        <p className="text-center mt-6 text-xs text-[var(--text-secondary)]">
          &copy; {new Date().getFullYear()} DynDev. {t('app.copyright')}
        </p>
      </div>
    </div>
  );
}
